import { Injectable } from '@angular/core';
import { 
    searchReportsByName, 
    searchReportsByExactName, 
    searchReportsWithModuleInfo,
    getReportsByModuleId,
    searchReportsWithModuleGrouping,
    ReportData,
    SavedReport 
} from '../helpers/report-search.utils';

@Injectable({
    providedIn: 'root'
})
export class ReportSearchService {

    constructor() { }

    /**
     * Search for reports by name across all modules
     * @param data - The complete report data object
     * @param searchTerm - The report name to search for (case-insensitive)
     * @returns Array of matching report objects
     */
    searchReportsByName(data: ReportData, searchTerm: string): SavedReport[] {
        return searchReportsByName(data, searchTerm);
    }

    /**
     * Search for reports by exact name match
     * @param data - The complete report data object
     * @param searchTerm - The exact report name to search for (case-insensitive)
     * @returns Array of matching report objects
     */
    searchReportsByExactName(data: ReportData, searchTerm: string): SavedReport[] {
        return searchReportsByExactName(data, searchTerm);
    }

    /**
     * Search for reports by name and return with module information
     * @param data - The complete report data object
     * @param searchTerm - The report name to search for (case-insensitive)
     * @returns Array of matching report objects with module info
     */
    searchReportsWithModuleInfo(data: ReportData, searchTerm: string): Array<SavedReport & { moduleName: string; moduleId: number }> {
        return searchReportsWithModuleInfo(data, searchTerm);
    }

    /**
     * Search for reports by name and group them by module
     * @param data - The complete report data object
     * @param searchTerm - The report name to search for (case-insensitive)
     * @returns Object with module names as keys and arrays of matching reports as values
     */
    searchReportsWithModuleGrouping(data: ReportData, searchTerm: string): { [moduleName: string]: SavedReport[] } {
        return searchReportsWithModuleGrouping(data, searchTerm);
    }

    /**
     * Get all reports from a specific module
     * @param data - The complete report data object
     * @param moduleId - The module ID to search in
     * @returns Array of report objects from the specified module
     */
    getReportsByModuleId(data: ReportData, moduleId: number): SavedReport[] {
        return getReportsByModuleId(data, moduleId);
    }

    /**
     * Search for reports by multiple criteria
     * @param data - The complete report data object
     * @param searchTerm - The report name to search for
     * @param moduleId - Optional module ID to filter by
     * @param isExactMatch - Whether to use exact match
     * @returns Array of matching report objects
     */
    searchReports(
        data: ReportData, 
        searchTerm: string, 
        moduleId?: number, 
        isExactMatch: boolean = false
    ): SavedReport[] {
        let results: SavedReport[] = [];

        if (isExactMatch) {
            results = this.searchReportsByExactName(data, searchTerm);
        } else {
            results = this.searchReportsByName(data, searchTerm);
        }

        // Filter by module if specified
        if (moduleId) {
            results = results.filter(report => report.parentModuleId === moduleId);
        }

        return results;
    }

    /**
     * Get all unique report names across all modules
     * @param data - The complete report data object
     * @returns Array of unique report names
     */
    getAllReportNames(data: ReportData): string[] {
        const reportNames: string[] = [];
        
        if (!data?.modules || !Array.isArray(data.modules)) {
            return reportNames;
        }

        data.modules.forEach(module => {
            if (module.savedReports && Array.isArray(module.savedReports)) {
                module.savedReports.forEach(report => {
                    if (report.reportName && !reportNames.includes(report.reportName)) {
                        reportNames.push(report.reportName);
                    }
                });
            }
        });

        return reportNames.sort();
    }

    /**
     * Get all module names
     * @param data - The complete report data object
     * @returns Array of module names
     */
    getModuleNames(data: ReportData): string[] {
        if (!data?.modules || !Array.isArray(data.modules)) {
            return [];
        }

        return data.modules.map(module => module.reportName).sort();
    }

    /**
     * Get module by ID
     * @param data - The complete report data object
     * @param moduleId - The module ID
     * @returns Module object or null if not found
     */
    getModuleById(data: ReportData, moduleId: number) {
        if (!data?.modules || !Array.isArray(data.modules)) {
            return null;
        }

        return data.modules.find(module => module.id === moduleId) || null;
    }

    /**
     * Get all reports grouped by parent module name (no search filtering)
     * @param data - The complete report data object
     * @returns Array of report objects with parent module information
     */
    getAllReportsGroupedByParent(data: ReportData): SavedReport[] {
        const groupedReports: SavedReport[] = [];
        
        if (!data?.modules || !Array.isArray(data.modules)) {
            return groupedReports;
        }
        
        // Iterate through all modules
        data.modules.forEach(module => {
            if (module.savedReports && Array.isArray(module.savedReports)) {
                // Add all saved reports from this module
                module.savedReports.forEach(report => {
                    // Add module information to each report for grouping context
                    const reportWithModuleInfo = {
                        ...report,
                        parentModuleName: module.reportName
                    };
                    groupedReports.push(reportWithModuleInfo);
                });
            }
        });
        
        return groupedReports;
    }
} 